import {
  makeStyles,
  mergeClasses,
  shorthands,
} from "@fluentui/react-components";
import type { AppIconPreviewGalleryButtonState } from "./AppIconPreviewGalleryButton.types";
import { customTokens } from "@1js/acui-theme";
// eslint-disable-next-line no-restricted-imports
import {
  HighContrastSelector,
  commonHighContrastColorButtonRest,
  commonMediumContrastColorHoverFocus,
  commonHighContrastColorDisabled,
  commonHighContrastColorHoverFocus,
  useCursorDisabledStyles,
} from "@1js/acui-common/lib/styling/next";
import type { GriffelStyle } from "@fluentui/react-components";
import { convertToCssObject } from "@1js/acui-common";

export const previewIconStyles = {
  root: {
    height: customTokens.spacingVerticalXL,
    width: customTokens.spacingHorizontalXL,
  },
};

export const appIconGalleryButton = {
  backgroundColor: "--acui-appIcongalleryButton__backgroundColor",
  maxWidth: "--acui-appIcongalleryButton__maxWidth",
  flexDirection: "--acui-appIcongalleryButton__flexDirection",
  height: "--acui-appIcongalleryButton__height",
  width: "--acui-appIcongalleryButton__width",
  justifyContent: "--acui-appIcongalleryButton__justifyContent",
  alignItems: "--acui-appIcongalleryButton__alignItems",
};

export const appLabel = {
  maxWidth: "--acui-appLabel__maxWidth",
  margin: "--acui-appLabel__margin",
};

export const textStylesForIcon = {
  margin: "0px 1px",
  textOverflow: "ellipsis",
  overflow: "hidden",
};

const selectedButtonHighContrastColor: GriffelStyle = {
  backgroundColor: "Highlight !important",
};

const labelHoverHighContrastColor: GriffelStyle = {
  ":hover": {
    ...commonMediumContrastColorHoverFocus,
  },
};

const normalModeButtonBackgroundColor: GriffelStyle = {
  backgroundColor: "transparent !important",
};

const commonDisabledStyle: GriffelStyle = {
  ...useCursorDisabledStyles,
  ":hover": {
    ["> label"]: {
      ...useCursorDisabledStyles,
    },
    ...normalModeButtonBackgroundColor,
  },
  ...normalModeButtonBackgroundColor,
};

const useButtonStyle = makeStyles({
  root: {
    backgroundColor: `var(${appIconGalleryButton.backgroundColor})`,
    width: `var(${appIconGalleryButton.width})`,
    [`${HighContrastSelector}`]: {
      ":hover": {
        ["> label"]: {
          ...commonMediumContrastColorHoverFocus,
        },
      },
    },
  },
  rootMLR: {
    flexDirection: `var(${appIconGalleryButton.flexDirection})` as
      | "row"
      | "column",
    maxWidth: `var(${appIconGalleryButton.maxWidth})`,
    ...shorthands.margin(customTokens.spacingVerticalXS),
    ...shorthands.padding(customTokens.spacingVerticalXS),
    height: `var(${appIconGalleryButton.height})`,
    width: `var(${appIconGalleryButton.width})`,
  },
  flexContainer: {
    justifyContent: `var(${appIconGalleryButton.justifyContent})`,
    alignItems: `var(${appIconGalleryButton.alignItems})`,
  },
  noButtonHeightWidthButIconStyleMLR: {
    height: "52px", // These values are used in cases when no button size is given but icon size is there, these values are well tested and approved.
    width: "68px",
  },
  noButtonHeightWidthIconStyle: {
    ...shorthands.padding(customTokens.spacingVerticalS),
  },
  disabled: {
    ...commonDisabledStyle,
    [`${HighContrastSelector}`]: {
      backgroundColor: "buttonface !important",
      ":hover": {
        ["> label"]: {
          ...useCursorDisabledStyles,
          ...commonHighContrastColorDisabled,
        },
      },
    },
  },
  disabledAndSelected: {
    ...commonDisabledStyle,
    [`${HighContrastSelector}`]: {
      ...selectedButtonHighContrastColor,
      ":hover": {
        ["> label"]: {
          ...useCursorDisabledStyles,
          ...commonHighContrastColorHoverFocus,
        },
        ...selectedButtonHighContrastColor,
      },
    },
  },
});

const useLabelStyles = makeStyles({
  label: {
    margin: `var(${appLabel.margin})`,
    maxWidth: `var(${appLabel.maxWidth})`,
    fontFamily: customTokens.fontFamilyBase,
    fontSize: customTokens.fontSizeBase200,
  },
  rest: {
    [HighContrastSelector]: {
      ...commonHighContrastColorButtonRest,
      ...labelHoverHighContrastColor,
    },
  },
  labelSelected: {
    [HighContrastSelector]: {
      ...commonHighContrastColorHoverFocus,
      ...labelHoverHighContrastColor,
    },
  },
  disabled: {
    ...useCursorDisabledStyles,
    [HighContrastSelector]: {
      ...commonHighContrastColorDisabled,
    },
  },
  disabledAndSelected: {
    ...useCursorDisabledStyles,
    [HighContrastSelector]: {
      ...commonHighContrastColorHoverFocus,
    },
  },
});

export const useCssVars = (state: AppIconPreviewGalleryButtonState) => {
  const previewButtonStyles = state.styles?.galleryButtonInPreview;
  const rootStyles = previewButtonStyles?.root;
  const properties = convertToCssObject(
    rootStyles &&
      Array.isArray(rootStyles) &&
      rootStyles.find((style) => style.height && style.width)
  );
  const label = convertToCssObject(previewButtonStyles?.label);

  state.root.style = {
    ...state.root.style,
    [`${appIconGalleryButton.width}`]: rootStyles?.width,
  };

  if (state.size?.toLocaleLowerCase() === "large") {
    state.root.style = {
      ...state.root.style,
      [`${appIconGalleryButton.flexDirection}`]:
        previewButtonStyles?.flexContainer?.flexDirection,
      [`${appIconGalleryButton.backgroundColor}`]:
        rootStyles?.length > 0 ? rootStyles[0].backgroundColor : undefined,
      [`${appIconGalleryButton.maxWidth}`]:
        rootStyles?.length > 0 ? rootStyles[0].maxWidth : undefined,
      [`${appIconGalleryButton.height}`]: properties?.height,
      [`${appIconGalleryButton.width}`]: properties?.width,
      [`${appIconGalleryButton.justifyContent}`]:
        previewButtonStyles?.flexContainer?.justifyContent,
      [`${appIconGalleryButton.alignItems}`]:
        previewButtonStyles?.flexContainer?.alignItems,
      [`${appLabel.maxWidth}`]: label.maxWidth,
      [`${appLabel.margin}`]: label.margin,
    };
  }
};

export const useAppIconPreviewGalleryButtonStyles = (
  state: AppIconPreviewGalleryButtonState
) => {
  useCssVars(state);
  const style = useButtonStyle();
  const labelStyle = useLabelStyles();
  const buttonStyle = state.styles?.galleryButtonInPreview?.flexContainer
    ? style.flexContainer
    : style.noButtonHeightWidthButIconStyleMLR;
  const isMlr = state.size?.toLocaleLowerCase() === "large";

  state.root.className = mergeClasses(
    style.root,
    state.disabled && !state.selected && style.disabled,
    state.disabled && state.selected && style.disabledAndSelected,
    isMlr && style.rootMLR,
    isMlr && buttonStyle,
    isMlr &&
      String(state.previewIconProps?.styles?.root?.height).includes("%") &&
      String(state.previewIconProps?.styles?.root?.width).includes("%") &&
      style.noButtonHeightWidthIconStyle,
    state.root.className
  );

  if (state.buttonLabel) {
    state.buttonLabel.className = mergeClasses(
      labelStyle.label,
      state.disabled && !state.selected
        ? labelStyle.disabled
        : state.selected
          ? labelStyle.labelSelected
          : labelStyle.rest,
      state.disabled && state.selected && labelStyle.disabledAndSelected,
      state.buttonLabel.className
    );
  }
};
